import {
    Box, Button, CardContent, Grid, Typography, TextField, MenuItem,
    Select,
    FormControl,
    InputLabel,
    ListSubheader,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { getDateTime, getDayOption, getMonthOption, getPayPeriods } from '../../../../utils/genericMethod';
import { useTQuery } from '../../../../utils/useTQuery';
import { CustomPagination } from '../../AdminComponents/CustomTable';
import DataTable from 'react-data-table-component';
import JobTimecardDetailsDialog from "../../../../components/DialogPopup/JobTimecardDetailsDialog";

export const PayrollAccounting = ({ selectedTab }) => {
    const [showResetButton, setShowResetButton] = useState(false);
    const payPeriods = useMemo(() => getPayPeriods(), []);
    const [period, setPeriod] = useState(null);
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [timecardDate, setTimecardDate] = useState('');
    const [customError, setCustomError] = useState('');
    const [selectedTimeCard, setSelectedTimeCard] = useState(null);

    const initialPayload = {
        facilityId: null,
        clinicianId: null,
        dates: {}, page: 1, rowsPerPage: 10
    }
    //Pagination 
    const [pagination, setPagination] = useState({
        page: 1,  // Zero-based for the first page
        rowsPerPage: 10,
        totalPage: 0,
    });
    const [payload, setPayload] = useState(initialPayload);
    const { data: facilityData, isLoading: isLoadingFacility } = useTQuery(["facilityWithClinician",], `/job/facilityWithClinician`, 'get');
    const { data, isLoading, refetch } = useTQuery(
        ["payroll", payload, selectedTab],
        selectedTab ? `/job/payrollAccounting` : null,
        'post',
        payload,
        selectedTab?.text === 'Payroll'
    );
    const [payrollData, setPayrollData] = useState([]);
    const [selectedFacilityId, setSelectedFacilityId] = useState('');
    const [selectedClinicianId, setSelectedClinicianId] = useState('');
    const facilityList = facilityData?.OUTPUT || [];
    const selectedFacility = facilityList?.find(f => f.facilityId === selectedFacilityId);
    const clinicians = selectedFacility?.clinicians || [];


    useEffect(() => {
        if (data?.STATUS) {
            setPayrollData(data?.OUTPUT || []);
            setPagination(prev => ({
                ...prev,
                rowsPerPage: data?.meta?.pageSize ?? 10,
                totalPage: data?.meta?.totalItems ?? 0
            }));
        } else {
            setPayrollData([]);
        }
    }, [selectedTab, data]);

    // pagination state is kept in sync with `payload` inside the
    // CustomPagination callbacks below, so we don’t need a separate effect.
    // (leftover from earlier implementation)


    const payrollColumn = [
        { name: 'Temp', selector: row => row.Temp, sortable: true },
        { name: 'Name', selector: row => row.Name, sortable: true },
        { name: 'Email', selector: row => row.EmailAddress, sortable: true },
        { name: 'Reconciled By', selector: row => row.ReconciledBy, sortable: true },
        { name: 'Shift Date', selector: row => new Date(row.ShiftDate).toDateString(), sortable: true },
        { name: 'Shift Time', selector: row => row.ShiftTime, sortable: true },
        { name: 'Period', selector: row => new Date(row.Period).toDateString(), sortable: true },
        { name: 'Dept', selector: row => row.Dept, sortable: true },
        { name: 'Qty', selector: row => row.TotalTime },
        { name: 'Rate', selector: row => `$${row.Rate}`, sortable: true },
        { name: 'Payment', selector: row => `$${row.Payment}`, sortable: true },
        // {
        //     name: 'View Payroll',
        //     button: true,
        //     center: true,
        //     cell: (row) => (
        //         <Button
        //             variant="contained"
        //             sx={{
        //                 backgroundColor: '#ccc',
        //                 color: '#000',
        //                 textTransform: 'none',
        //                 '&:hover': { backgroundColor: '#bbb' }
        //             }}
        //             onClick={() => setSelectedTimeCard(row)}
        //         >
        //             Open
        //         </Button>
        //     )
        // }
    ];

    const handleReset = () => {
        setPeriod(null);
        setCustomFrom('');
        setCustomTo('');
        setTimecardDate('');
        setSelectedFacilityId('');
        setSelectedClinicianId('');
        setPayload(initialPayload);
        setShowResetButton(false);
    };

    const handleFilterClick = () => {
        const filter = getActiveFilter();
        if (filter.type === 'error') {
            setCustomError(filter.message);
            return;
        }
        const dates = getFilterDates();
        if (dates) {
            setShowResetButton(true);
        }
        const newPayload = {
            ...payload,
            facilityId: selectedFacilityId,
            clinicianId: selectedClinicianId,
            dates,
        };
        if (selectedFacilityId) {
            setShowResetButton(true);
        }

        setPayload(newPayload); // update state (query will rerun automatically)
    };



    const getActiveFilter = () => {
        if (period) return { type: 'Period', message: 'Periods Filter is selected' };
        if (customFrom || customTo) {
            if (!customFrom || !customTo) {
                return { type: 'error', message: 'Select both Custom Date From and To' };
            }
            return { type: 'Custom Date', message: 'Custom Dates Filter is selected' };
        }
        if (timecardDate) return { type: 'Timecard', message: 'Timecard Dates Filter is selected' };
        return { type: null, message: '' };
    };

    const handlePeriodChange = (e) => {
        const value = JSON.parse(e.target.value);
        setPeriod(value);
        setCustomFrom('');
        setCustomTo('');
        setTimecardDate('');
        setCustomError('');
    };
    const handleCustomDateChange = (type, value) => {
        setPeriod(null);
        setTimecardDate('');
        setCustomError('');
        if (type === 'from') setCustomFrom(value);
        else setCustomTo(value);
    };

    const validateCustomDates = () => {
        setCustomError(''); // Reset first

        if (!customFrom || !customTo) {
            setCustomError('Select both Custom Date From and To');
            return;
        }

        const from = new Date(customFrom);
        const to = new Date(customTo);
        if (to < from) {
            setCustomError('"Custom To" date must be later than or equal to "Custom From" date');
            return;
        }
    };
    const getFilterDates = () => {
        if (period) {
            return { from: period.from, to: period.to };
        }
        if (customFrom && customTo) {

            const from = new Date(customFrom);
            const to = new Date(customTo);
            if (to < from) {
                setCustomError('"Custom To" date must be later than or equal to "Custom From" date');
                return;
            }
            return {
                from: getDateTime(from),
                to: getDateTime(to, true),
            };
        }
        if (timecardDate) {
            return {
                from: getDateTime(new Date('2000-01-01')),
                to: getDateTime(new Date(timecardDate), true),
            };
        }
        return null;
    };

    const handleSort = (column, sortDirection) => {
        const fieldMapping = {
            'Temp': 'ID',
            'Name': 'user.FULL_NAME',
            'Email': 'user.EMAIL_ADDRESS',
            // 'Reconciled By': 'job.user.FACILITY_NAME',
            // 'Shift Date': 'job_service.JOB_DATE',
            // 'Shift Time': 'job_service.JOB_IN_TIME',
            // 'Period': 'PERIOD',
            // 'Dept': 'DEPARTMENT',
            // 'Qty': 'TOTAL_TIME',
            // 'Rate': 'RATE',
            // 'Payment': 'PAYMENT'
        };

        const fieldName = fieldMapping[column.name] || column.name;

        // update payload and reset to first page; react-query will refetch
        setPayload(prev => ({
            ...prev,
            FIELD: fieldName,
            DIRECTION: sortDirection.toUpperCase(),
            page: 1
        }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const safePayrollData = payrollData.map((item, index) => ({
    ...item,
    uniqueKey: `${item.ID}-${index}-${pagination.page}`
}));

    return (
        <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                Payroll <span style={{ color: "#6ebe44" }}>Manager</span>
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <InputLabel id="period-label">Period</InputLabel>
                        <Select
                            labelId="period-label"
                            value={period ? JSON.stringify(period) : ''}
                            label="Period"
                            onChange={handlePeriodChange}
                        >
                            <ListSubheader>Pay Period</ListSubheader>
                            {payPeriods.map((period, index) => (
                                <MenuItem key={`pay-${index}`} value={JSON.stringify(period)}>
                                    {period.label}
                                </MenuItem>
                            ))}

                            <ListSubheader>Days</ListSubheader>
                            {['Yesterday', 'Today', 'Tomorrow'].map((day) => {
                                const option = getDayOption(day);
                                return (
                                    <MenuItem key={day} value={JSON.stringify(option)}>
                                        {option.label}
                                    </MenuItem>
                                );
                            })}

                            <ListSubheader>Months</ListSubheader>
                            {['Last Month', 'This Month'].map((month) => {
                                const option = getMonthOption(month);
                                return (
                                    <MenuItem key={month} value={JSON.stringify(option)}>
                                        {option.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                {/* Custom Date From */}
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <TextField
                            fullWidth
                            label="Custom From"
                            type="date"
                            variant="outlined"
                            size="small"
                            InputProps={{
                                className: "picker_datefield"
                            }}
                            value={customFrom}
                            onChange={(e) => handleCustomDateChange('from', e.target.value)}
                            onBlur={validateCustomDates}
                            InputLabelProps={{ shrink: true }}
                            error={!!customError}
                        />
                    </FormControl>
                </Grid>

                {/* Custom Date To */}
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <TextField
                            fullWidth
                            label="Custom To"
                            type="date"
                            variant="outlined"
                            InputProps={{
                                className: "picker_datefield"
                            }}
                            size="small"
                            value={customTo}
                            onChange={(e) => handleCustomDateChange('to', e.target.value)}
                            onBlur={validateCustomDates}
                            InputLabelProps={{ shrink: true }}
                            error={!!customError}
                            helperText={customError}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <InputLabel>Facility</InputLabel>
                        <Select
                            value={selectedFacilityId}
                            label="Facility"
                            onChange={(e) => {
                                setSelectedFacilityId(e.target.value);
                                setSelectedClinicianId(''); // Reset clinician on facility change
                            }}
                        >
                            {facilityList?.map((facility) => (
                                <MenuItem key={facility.facilityId} value={facility.facilityId}>
                                    {facility.facilityName} {facility.city ? `(${facility.city})` : ""}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth disabled={!selectedFacilityId}>
                        <InputLabel>Clinician</InputLabel>
                        <Select
                            value={selectedClinicianId}
                            label="Clinician"
                            onChange={(e) => setSelectedClinicianId(e.target.value)}
                        >
                            {clinicians?.map((clinician) => (
                                <MenuItem key={clinician.clinicianId} value={clinician.clinicianId}>
                                    {clinician.clinicianName} ({clinician.clinicianRoleName})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* <Typography variant="h6" fontWeight="bold" mt={2}>
                Payroll <span style={{ color: "#6ebe44" }}>Options</span>
            </Typography>      
            <Grid container spacing={2}>
                <Grid item lg={6}>
                    <Grid item xs={12} sm={6} md={6} display="flex" alignItems="center">
                        <Checkbox disabled />
                        <Typography>Preview Incomplete Payroll</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={9} display="flex" alignItems="center">
                        <Checkbox disabled />
                        <Typography>Complete Payroll and mark it complete</Typography>
                    </Grid>

                </Grid>
                <Grid item lg={6}>
                    <Grid item xs={12} sm={6} md={6} display="flex" alignItems="center">
                        <Checkbox disabled />
                        <Typography>View complete Payroll</Typography>
                    </Grid>


                    <Grid item xs={12} sm={6} md={9} display="flex" alignItems="center">
                        <Checkbox disabled />
                        <Typography>View complete Payroll Batch</Typography>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        fullWidth
                        label="Paycheck Date"
                        type="date"
                        variant="outlined"
                        InputProps={{
                            className: "picker_datefield"
                        }}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

            </Grid> */}


            <Box textAlign="right" mt={2}>
                {showResetButton && <Button variant="outlined" onClick={handleReset}>
                    Reset
                </Button>}
                <Button variant="contained" sx={{ background: "grey", color: "#fff", textTransform: "none", ml: 1 }} onClick={handleFilterClick}>
                    Run Payroll
                </Button>
            </Box>
            {/* Table */}
            <Box mt={2} sx={{ width: '100%' }}>

                <DataTable
                    className="table-items"
                    columns={payrollColumn}
                    data={safePayrollData}
                    keyField="uniqueKey"
                    sortServer
                    onSort={handleSort}
                    progressPending={isLoading}
                    progressComponent={<div>Loading Payrolls ...</div>}
                    fixedHeader
                    fixedHeaderScrollHeight="600px"
                />
                <CustomPagination
                    count={pagination.totalPage}
                    rowsPerPage={pagination.rowsPerPage}
                    page={pagination.page - 1}
                    setPage={(newPage) => {
                        setPagination(prev => ({ ...prev, page: newPage + 1 }));
                    }}
                    onChangePage={(newPage) => {
                        setPagination(prev => ({ ...prev, page: newPage + 1 }));
                        // keep payload in sync so queryKey changes immediately
                        setPayload(prev => ({ ...prev, page: newPage + 1 }));
                    }}
                    onChangeRowsPerPage={(rowsPerPage) => {
                        setPagination(prev => ({ ...prev, rowsPerPage, page: 1 }));
                        setPayload(prev => ({ ...prev, rowsPerPage, page: 1 }));
                    }}
                />
            </Box>

            {selectedTimeCard && <JobTimecardDetailsDialog open={selectedTimeCard} onClose={() => setSelectedTimeCard(null)} data={selectedTimeCard} />}
        </CardContent>
    );
};






